// src/components/CategoryForm.js
// Création / modification d'une catégorie de ressources.
// Une catégorie peut être rattachée à UN ou PLUSIEURS cours (courseIds) :
// elle est alors partagée, et une ressource ajoutée dedans apparaît dans
// chacun de ces cours (admin panel + app).
// courseId (premier cours coché) est conservé pour la compatibilité avec
// les anciens écrans qui ne lisent que ce champ.
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
  collection, getDocs, addDoc, doc, updateDoc, getDoc, serverTimestamp
} from 'firebase/firestore';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

function CategoryForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const [courses, setCourses] = useState([]);
  const [courseSearch, setCourseSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    courseIds: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const goBack = () => {
    // Retour à la page d'où l'on vient (Centre de ressources ou Catégories)
    if (window.history.length > 1) navigate(-1);
    else navigate('/resources');
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
        const coursesData = coursesSnapshot.docs.map(d => ({
          id: d.id,
          title: d.data().title || 'Cours sans titre'
        }));
        coursesData.sort((a, b) => a.title.localeCompare(b.title));
        setCourses(coursesData);

        if (id) {
          const categoryDoc = await getDoc(doc(db, 'resource_categories', id));
          if (categoryDoc.exists()) {
            const data = categoryDoc.data();
            const ids = Array.isArray(data.courseIds) && data.courseIds.length > 0
              ? data.courseIds
              : (data.courseId ? [data.courseId] : []);
            setFormData({
              name: data.name || '',
              description: data.description || '',
              courseIds: ids
            });
          } else {
            setError('Catégorie introuvable.');
          }
        } else {
          // Pré-sélection depuis ?courseId=... (lien depuis un cours)
          const preselected = new URLSearchParams(location.search).get('courseId');
          if (preselected) {
            setFormData(prev => ({ ...prev, courseIds: [preselected] }));
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement:', err);
        setError('Erreur lors du chargement des données');
        setLoading(false);
      }
    };

    fetchData();
  }, [id, location.search]);

  const toggleCourse = (courseId) => {
    setFormData(prev => ({
      ...prev,
      courseIds: prev.courseIds.includes(courseId)
        ? prev.courseIds.filter(c => c !== courseId)
        : [...prev.courseIds, courseId]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('Veuillez saisir un nom pour la catégorie.');
      return;
    }
    if (formData.courseIds.length === 0) {
      setError('Veuillez sélectionner au moins un cours.');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        courseIds: formData.courseIds,
        courseId: formData.courseIds[0], // compatibilité anciens écrans
        updatedAt: serverTimestamp()
      };

      if (id) {
        // updateDoc ne touche que ces champs : les autres champs éventuels
        // de la catégorie sont conservés.
        await updateDoc(doc(db, 'resource_categories', id), dataToSave);
      } else {
        await addDoc(collection(db, 'resource_categories'), {
          ...dataToSave,
          createdAt: serverTimestamp()
        });
      }

      goBack();
    } catch (err) {
      console.error("Erreur lors de l'enregistrement:", err);
      setError(err.message || "Une erreur est survenue lors de l'enregistrement.");
      setSaving(false);
    }
  };

  const filteredCourses = courses.filter(c =>
    c.title.toLowerCase().includes(courseSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Chargement en cours...</p>
      </div>
    );
  }

  return (
    <div className="resource-form-container">
      <h1>{id ? 'Modifier' : 'Créer'} une catégorie</h1>
      <p style={{ color: '#666', marginBottom: 20 }}>
        Cochez plusieurs cours pour partager la même catégorie : les ressources
        qu'elle contient apparaîtront dans chacun de ces cours.
      </p>

      {error && (
        <div className="error-message">
          <p>{error}</p>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label>Nom de la catégorie *</label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Ex: Chorégraphies, Musiques, Échauffements..."
            required
          />
        </div>

        <div className="form-group">
          <label>Description</label>
          <textarea
            rows="3"
            value={formData.description}
            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          />
        </div>

        <div className="form-group">
          <label>Cours concernés *</label>
          <input
            type="text"
            placeholder="Rechercher un cours..."
            value={courseSearch}
            onChange={(e) => setCourseSearch(e.target.value)}
            style={{ marginBottom: 10 }}
          />
          <div
            style={{
              maxHeight: 280,
              overflowY: 'auto',
              border: '1px solid #ddd',
              borderRadius: 8,
              padding: 10
            }}
          >
            {filteredCourses.length === 0 ? (
              <p style={{ color: '#999', margin: 0 }}>Aucun cours trouvé</p>
            ) : (
              filteredCourses.map(course => (
                <label
                  key={course.id}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={formData.courseIds.includes(course.id)}
                    onChange={() => toggleCourse(course.id)}
                  />
                  {course.title}
                </label>
              ))
            )}
          </div>
          <small>{formData.courseIds.length} cours sélectionné(s)</small>
        </div>

        <div className="form-actions">
          <button type="button" className="cancel-button" onClick={goBack} disabled={saving}>
            Annuler
          </button>
          <button type="submit" className="submit-button" disabled={saving}>
            {saving ? 'Enregistrement...' : id ? 'Mettre à jour' : 'Créer la catégorie'}
          </button>
        </div>
      </form>
    </div>
  );
}

export default CategoryForm;